import ArticleShell from "@/components/site/ArticleShell";

const BodyEs = () => (
  <div className="article-body">
    <p>Son las 22:47 de la noche anterior a tu ciclo. Miras el techo. El reloj marca la cuenta atrás. Sabes que mañana a las 09:30 estarás sentada en el sillón de quimio y no hay forma de que tu cuerpo se relaje ahora. Y lo peor: <strong>ya empezaste a tener náuseas hace tres días, cuando todavía no había ninguna sustancia en tu sangre</strong>.</p>
    <p>Esto tiene nombre clínico: <strong>ansiedad anticipatoria a la quimioterapia</strong>. No es exageración, no es debilidad y no es "estar pensando demasiado". Es una respuesta biológica real que afecta hasta al 40% de las personas en tratamiento oncológico según la literatura reciente.</p>

    <h2>Por qué el cuerpo se anticipa</h2>
    <p>Tras uno o dos ciclos, tu cerebro asocia estímulos ambientales (el olor del hospital, la sala de espera, el uniforme sanitario, la vía en el brazo) con las molestias posteriores del tratamiento. Es <strong>condicionamiento clásico</strong> — el mismo mecanismo por el que a Pavlov se le hacían la boca agua a los perros oyendo una campana. Solo que aquí no es saliva: son náuseas, taquicardia, insomnio, llanto sin motivo.</p>
    <p>Esto significa dos cosas importantes:</p>
    <ul>
      <li>Tu cuerpo <strong>no está fallando</strong>: está aprendiendo con mucha eficiencia (el problema es qué está aprendiendo).</li>
      <li>Se puede <strong>desaprender</strong>. La neuroplasticidad funciona en ambas direcciones.</li>
    </ul>

    <h2>Los tres momentos de la ansiedad de quimio</h2>

    <h3>1 · La noche anterior (o los 2-3 días previos)</h3>
    <p>Insomnio, náuseas anticipatorias, opresión torácica, irritabilidad. Muchas mujeres describen que el domingo por la tarde ya notan cómo el cuerpo "se pone en modo lunes de quimio".</p>

    <h3>2 · El propio día (camino al hospital)</h3>
    <p>Aumento de la frecuencia cardíaca, sudoración, mareo, "quiero salir corriendo". Algunas pacientes vomitan antes de llegar al hospital sin haber recibido todavía ninguna medicación. Esto es <strong>náusea anticipatoria pura</strong>, un fenómeno reconocido en oncología.</p>

    <h3>3 · Los días 3-5 post-ciclo</h3>
    <p>Cuando bajan los corticoides que te ponen con la quimio, aparece un valle de ansiedad + tristeza. No es debilidad emocional: es el cuerpo bajando de un chute farmacológico. Suele llamarse "el bajón del día 4" y es predecible.</p>

    <h2>Herramientas concretas para cada momento</h2>

    <h3>Para la noche anterior</h3>
    <ul>
      <li><strong>Práctica de exhalación 4-6</strong> durante 5 minutos antes de dormir: inhala 4 segundos, exhala 6. Activa el nervio vago y reduce cortisol.</li>
      <li><strong>Rutina de "aterrizaje sensorial"</strong>: baja las luces, ducha templada larga, música conocida a bajo volumen, evita pantallas 45 minutos antes.</li>
      <li>Si el insomnio ya está instalado y llevas 30 min sin dormir: levántate 15 min, lee algo neutro, vuelve. No pelees con la cama.</li>
    </ul>

    <h3>Para el propio día</h3>
    <ul>
      <li><strong>Cambia los estímulos condicionados</strong>: si siempre vas con la misma ropa al hospital, cámbiala. Si escuchas siempre la misma emisora, cambia a un podcast. Rompes la asociación.</li>
      <li><strong>Anclaje corporal en el trayecto</strong>: 5 puntos de contacto — pies en el suelo, manos en el regazo, espalda contra el asiento. Nombra en voz baja: "veo… oigo… siento…".</li>
      <li><strong>Habla con enfermería</strong>: en la mayoría de hospitales se puede pedir un ansiolítico específico para la premedicación si la ansiedad anticipatoria es alta. No es rendirse; es tratamiento adecuado.</li>
    </ul>

    <h3>Para el valle del día 3-5</h3>
    <ul>
      <li><strong>Normalízalo</strong>: sabiendo que va a llegar, no te asustas cuando llega. Márcatelo en el calendario.</li>
      <li><strong>Movimiento muy suave</strong>: 10 minutos de paseo suave, yoga restaurativo tumbada, respiración. La quietud absoluta suele empeorar el bajón.</li>
      <li><strong>Llama a alguien</strong>: es el día en que más cuesta pedir compañía y el que más la necesita. Prepáralo antes: pídele a alguien de confianza que te llame ese día.</li>
    </ul>

    <h2>Qué NO ayuda (y muchos te lo dirán)</h2>
    <ul>
      <li>"Piensa en positivo" — no funciona con condicionamiento neuronal.</li>
      <li>"Distráete" — la distracción sostenida agota y no aborda la causa.</li>
      <li>Alcohol o comida como bajada — refuerzan el bucle.</li>
      <li>Aguantar sola — la ansiedad no tratada empeora ciclo tras ciclo.</li>
    </ul>

    <h2>Cuándo pedir apoyo profesional</h2>
    <p>Habla con tu equipo médico o con psicooncología si:</p>
    <ul>
      <li>La ansiedad te lleva a plantearte <strong>abandonar el tratamiento</strong>.</li>
      <li>Tienes <strong>ataques de pánico</strong> repetidos.</li>
      <li>Vomitas antes de llegar al hospital (náusea anticipatoria pura).</li>
      <li>No duermes en absoluto la noche anterior a cada ciclo.</li>
    </ul>
    <p>La psicooncología pública existe en la mayoría de hospitales españoles. Muchas asociaciones (AECC, GEPAC) ofrecen apoyo gratuito. No es un lujo: es parte del tratamiento adecuado.</p>

    <h2>Lecturas relacionadas</h2>
    <ul>
      <li><a href="/blog/ansiedad-despues-cancer">Ansiedad después del cáncer: por qué persiste y cómo trabajar con ella</a></li>
      <li><a href="/blog/ataques-panico-despues-cancer">Ataques de pánico después del cáncer: cómo identificarlos y qué hacer</a></li>
      <li><a href="/blog/autocompasion-cancer">Autocompasión cuando el cuerpo falla</a></li>
    </ul>

    <p>Si vives con ansiedad durante la quimio y necesitas herramientas cuerpo-mente concretas, la <a href="/sesion-gratuita">primera sesión conmigo es gratuita</a>.</p>
    <p>— Marta Montardit</p>
  </div>
);

const BodyCa = () => (
  <div className="article-body">
    <p>Són les 22:47 de la nit anterior al teu cicle. Mires al sostre. El rellotge marca el compte enrere. Saps que demà a les 09:30 estaràs asseguda al sillón de quimio i no hi ha manera que el teu cos es relaxi ara. I el pitjor: <strong>ja vas començar a tenir nàusees fa tres dies, quan encara no hi havia cap substància a la teva sang</strong>.</p>
    <p>Això té nom clínic: <strong>ansietat anticipatòria a la quimioteràpia</strong>. No és exageració, no és debilitat i no és "estar pensant massa". És una resposta biològica real que afecta fins al 40% de les persones en tractament oncològic segons la literatura recent.</p>

    <h2>Per què el cos s'anticipa</h2>
    <p>Després d'un o dos cicles, el teu cervell associa estímuls ambientals (l'olor de l'hospital, la sala d'espera, l'uniforme sanitari, la via al braç) amb les molèsties posteriors del tractament. És <strong>condicionament clàssic</strong> — el mateix mecanisme pel qual a Pavlov se li feia la boca aigua als gossos escoltant una campana. Només que aquí no és saliva: són nàusees, taquicàrdia, insomni, plor sense motiu.</p>
    <p>Això vol dir dues coses importants:</p>
    <ul>
      <li>El teu cos <strong>no està fallant</strong>: està aprenent amb molta eficiència (el problema és què està aprenent).</li>
      <li>Es pot <strong>desaprendre</strong>. La neuroplasticitat funciona en ambdues direccions.</li>
    </ul>

    <h2>Els tres moments de l'ansietat de quimio</h2>

    <h3>1 · La nit anterior (o els 2-3 dies previs)</h3>
    <p>Insomni, nàusees anticipatòries, opressió toràcica, irritabilitat. Moltes dones descriuen que el diumenge a la tarda ja noten com el cos "es posa en mode dilluns de quimio".</p>

    <h3>2 · El mateix dia (camí a l'hospital)</h3>
    <p>Augment de la freqüència cardíaca, sudoració, marejos, "vull sortir corrents". Algunes pacients vomiten abans d'arribar a l'hospital sense haver rebut encara cap medicació. Això és <strong>nàusea anticipatòria pura</strong>, un fenomen reconegut en oncologia.</p>

    <h3>3 · Els dies 3-5 post-cicle</h3>
    <p>Quan baixen els corticoides que et posen amb la quimio, apareix una vall d'ansietat + tristesa. No és debilitat emocional: és el cos baixant d'un xut farmacològic. Se sol dir "la baixada del dia 4" i és predictible.</p>

    <h2>Eines concretes per a cada moment</h2>

    <h3>Per a la nit anterior</h3>
    <ul>
      <li><strong>Pràctica d'exhalació 4-6</strong> durant 5 minuts abans de dormir: inhala 4 segons, exhala 6. Activa el nervi vague i redueix cortisol.</li>
      <li><strong>Rutina d'"aterratge sensorial"</strong>: abaixa els llums, dutxa temperada llarga, música coneguda a volum baix, evita pantalles 45 minuts abans.</li>
      <li>Si l'insomni ja està instal·lat i portes 30 min sense dormir: aixeca't 15 min, llegeix alguna cosa neutra, torna. No lluitis amb el llit.</li>
    </ul>

    <h3>Per al mateix dia</h3>
    <ul>
      <li><strong>Canvia els estímuls condicionats</strong>: si sempre vas amb la mateixa roba a l'hospital, canvia-la. Si escoltes sempre la mateixa emissora, canvia a un podcast. Trenques l'associació.</li>
      <li><strong>Ancoratge corporal al trajecte</strong>: 5 punts de contacte — peus a terra, mans a la falda, esquena contra el seient. Anomena en veu baixa: "veig… escolto… sento…".</li>
      <li><strong>Parla amb infermeria</strong>: a la majoria d'hospitals es pot demanar un ansiolític específic per a la premedicació si l'ansietat anticipatòria és alta. No és rendir-se; és tractament adequat.</li>
    </ul>

    <h3>Per a la vall del dia 3-5</h3>
    <ul>
      <li><strong>Normalitza-ho</strong>: sabent que arribarà, no t'espantes quan arriba. Marca-t'ho al calendari.</li>
      <li><strong>Moviment molt suau</strong>: 10 minuts de passejada suau, ioga restauratiu estirada, respiració. La quietud absoluta sol empitjorar la baixada.</li>
      <li><strong>Truca a algú</strong>: és el dia en què més costa demanar companyia i el que més la necessita. Prepara-ho abans: demana a algú de confiança que et truqui aquell dia.</li>
    </ul>

    <h2>Què NO ajuda (i molts t'ho diran)</h2>
    <ul>
      <li>"Pensa en positiu" — no funciona amb condicionament neuronal.</li>
      <li>"Distreu-te" — la distracció sostinguda esgota i no aborda la causa.</li>
      <li>Alcohol o menjar com a baixada — reforcen el bucle.</li>
      <li>Aguantar sola — l'ansietat no tractada empitjora cicle rere cicle.</li>
    </ul>

    <h2>Quan demanar suport professional</h2>
    <p>Parla amb el teu equip mèdic o amb psicooncologia si:</p>
    <ul>
      <li>L'ansietat et porta a plantejar-te <strong>abandonar el tractament</strong>.</li>
      <li>Tens <strong>atacs de pànic</strong> repetits.</li>
      <li>Vomites abans d'arribar a l'hospital (nàusea anticipatòria pura).</li>
      <li>No dorms gens la nit anterior a cada cicle.</li>
    </ul>
    <p>La psicooncologia pública existeix a la majoria d'hospitals catalans i espanyols. Moltes associacions (AECC, GEPAC) ofereixen suport gratuït. No és un luxe: és part del tractament adequat.</p>

    <h2>Lectures relacionades</h2>
    <ul>
      <li><a href="/blog/ansiedad-despues-cancer">Ansietat després del càncer: per què persisteix i com treballar-la</a></li>
      <li><a href="/blog/ataques-panico-despues-cancer">Atacs de pànic després del càncer: com identificar-los i què fer</a></li>
      <li><a href="/blog/autocompasion-cancer">Autocompassió quan el cos falla</a></li>
    </ul>

    <p>Si vius amb ansietat durant la quimio i necessites eines cos-ment concretes, la <a href="/sesion-gratuita">primera sessió amb mi és gratuïta</a>.</p>
    <p>— Marta Montardit</p>
  </div>
);

const ArticleAnsiedadQuimioterapia = () => (
  <ArticleShell
    bodyEs={<BodyEs />}
    bodyCa={<BodyCa />}
    leadMagnet={{
      guide: "post-cancer",
      source: "blog/ansiedad-quimioterapia",
      title: {
        es: "Guía: Volver a habitar el cuerpo después del cáncer",
        ca: "Guia: Tornar a habitar el cos després del càncer",
      },
      subtitle: {
        es: "Prácticas cuerpo-mente para las semanas y meses tras el tratamiento.",
        ca: "Pràctiques cos-ment per a les setmanes i mesos després del tractament.",
      },
      bullets: {
        es: [
          "Práctica de 5 minutos para la ansiedad anticipatoria",
          "Cómo estructurar el descanso entre ciclos",
          "La recibes en tu email en menos de 5 minutos",
        ],
        ca: [
          "Pràctica de 5 minuts per a l'ansietat anticipatòria",
          "Com estructurar el descans entre cicles",
          "La reps al teu email en menys de 5 minuts",
        ],
      },
    }}
  />
);

export default ArticleAnsiedadQuimioterapia;
